import { useState } from "react";
import { Printer, Quote, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

/**
 * PosterTemplatesSection — affiliate print material downloader.
 *
 * Renders a language picker + three variant cards (classic portrait,
 * landscape banner, pull-quote review). Each card previews the layout
 * with a compact inline SVG so affiliates can pick before downloading.
 * The quote card also offers optional custom testimonial fields.
 */
const LANGS = [
  { code: "nl", flag: "🇳🇱", label: "Nederlands" },
  { code: "en", flag: "🇬🇧", label: "English" },
  { code: "de", flag: "🇩🇪", label: "Deutsch" },
  { code: "fr", flag: "🇫🇷", label: "Français" },
  { code: "es", flag: "🇪🇸", label: "Español" },
];

function ClassicPreview() {
  // Editorial A4-portrait mock (v3): thin ink top strip, cream cover with
  // headline + trust seal, three numbered steps in indigo/amber/coral, a
  // sage quote band, centred QR card, dark ribbon bottom. Aspect ≈ 210:297.
  return (
    <svg viewBox="0 0 84 118" className="w-full h-auto border border-neutral-200" aria-hidden>
      <rect x="0" y="0" width="84" height="118" fill="#FFFFFF" />
      {/* Header cover */}
      <rect x="0" y="0" width="84" height="38" fill="#FAF7F1" />
      <rect x="0" y="0" width="84" height="1.4" fill="#0F0F10" />
      <rect x="6" y="6" width="18" height="1.6" fill="#3B4A9C" />
      <rect x="6" y="12" width="56" height="4" fill="#0F0F10" />
      <rect x="6" y="18" width="46" height="4" fill="#0F0F10" />
      <rect x="6" y="28" width="60" height="1.4" fill="#6B6560" opacity="0.55" />
      <rect x="6" y="31" width="52" height="1.4" fill="#6B6560" opacity="0.55" />
      {/* Trust seal top right */}
      <circle cx="74" cy="14" r="6" fill="#FAF7F1" stroke="#D9A441" strokeWidth="0.8" />
      <text x="74" y="15" fontSize="4" textAnchor="middle" fill="#0F0F10" fontWeight="bold">4,9</text>
      {/* 3 numbered step cards */}
      <g>
        <rect x="6" y="43" width="22" height="17" fill="#E4E7F4" />
        <rect x="6" y="43" width="22" height="0.9" fill="#3B4A9C" />
        <text x="9" y="56" fontSize="11" fill="#3B4A9C" fontFamily="Times, serif" fontWeight="bold">1</text>
        <rect x="16" y="49" width="10" height="1.4" fill="#0F0F10" opacity="0.8" />
        <rect x="16" y="52" width="8" height="1.4" fill="#0F0F10" opacity="0.6" />

        <rect x="31" y="43" width="22" height="17" fill="#FAF7F1" />
        <rect x="31" y="43" width="22" height="0.9" fill="#D9A441" />
        <text x="34" y="56" fontSize="11" fill="#D9A441" fontFamily="Times, serif" fontWeight="bold">2</text>
        <rect x="41" y="49" width="10" height="1.4" fill="#0F0F10" opacity="0.8" />
        <rect x="41" y="52" width="8" height="1.4" fill="#0F0F10" opacity="0.6" />

        <rect x="56" y="43" width="22" height="17" fill="#FAF7F1" />
        <rect x="56" y="43" width="22" height="0.9" fill="#E8543E" />
        <text x="59" y="56" fontSize="11" fill="#E8543E" fontFamily="Times, serif" fontWeight="bold">3</text>
        <rect x="66" y="49" width="10" height="1.4" fill="#0F0F10" opacity="0.8" />
        <rect x="66" y="52" width="8" height="1.4" fill="#0F0F10" opacity="0.6" />
      </g>
      {/* Sage quote band */}
      <rect x="0" y="63" width="84" height="10" fill="#EAF0E5" />
      <rect x="0" y="63" width="1.2" height="10" fill="#5C7A5C" />
      <text x="4" y="70" fontSize="7" fill="#5C7A5C" fontFamily="Times, serif" fontWeight="bold">“</text>
      <rect x="10" y="66" width="52" height="1.4" fill="#0F0F10" opacity="0.75" />
      <rect x="10" y="69" width="30" height="1.2" fill="#6B6560" />
      {/* QR card */}
      <rect x="20" y="78" width="44" height="26" fill="#F1EEE6" />
      <rect x="28" y="82" width="28" height="4" fill="#0F0F10" />
      <rect x="32" y="88" width="20" height="14" fill="#0F0F10" />
      <rect x="34" y="90" width="3" height="3" fill="#F1EEE6" />
      <rect x="46" y="90" width="4" height="3" fill="#F1EEE6" />
      <rect x="34" y="98" width="4" height="3" fill="#F1EEE6" />
      {/* Ink ribbon */}
      <rect x="0" y="110" width="84" height="8" fill="#0F0F10" />
      <rect x="70" y="112" width="12" height="4" rx="2" fill="#E8543E" />
    </svg>
  );
}

function BannerPreview() {
  // A4-landscape mock: amber left strip, headline + bullets left,
  // QR frame right. Aspect ≈ 297:210.
  return (
    <svg viewBox="0 0 118 84" className="w-full h-auto border border-neutral-200" aria-hidden>
      <rect x="0" y="0" width="118" height="84" fill="#F9F8F6" />
      <rect x="0" y="0" width="5" height="84" fill="#B08D3F" />
      <rect x="10" y="8" width="16" height="1.5" fill="#B08D3F" />
      <rect x="10" y="14" width="50" height="4" fill="#1A1A1A" />
      <rect x="10" y="21" width="42" height="4" fill="#1A1A1A" />
      <rect x="10" y="30" width="55" height="1.5" fill="#5A554E" opacity="0.5" />
      <rect x="10" y="33" width="50" height="1.5" fill="#5A554E" opacity="0.5" />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="10" y={44 + i * 5} width="2" height="2" fill="#B08D3F" />
          <rect x="14" y={44 + i * 5} width="35" height="1.5" fill="#1A1A1A" opacity="0.7" />
        </g>
      ))}
      <rect x="10" y="66" width="24" height="6" rx="3" fill="#B08D3F" />
      <rect x="70" y="12" width="44" height="60" fill="#F0EFEA" />
      <rect x="79" y="20" width="26" height="1.8" fill="#1A1A1A" />
      <rect x="82" y="30" width="20" height="20" fill="#1A1A1A" />
      <rect x="85" y="33" width="3" height="3" fill="#F9F8F6" />
      <rect x="93" y="33" width="6" height="3" fill="#F9F8F6" />
      <rect x="85" y="43" width="6" height="3" fill="#F9F8F6" />
      <rect x="96" y="45" width="3" height="3" fill="#F9F8F6" />
      <rect x="79" y="56" width="26" height="1.5" fill="#1A1A1A" opacity="0.6" />
    </svg>
  );
}

function QuotePreview() {
  // A4-portrait mock: rose accent, star row, huge open-quote glyph, italic
  // quote lines, attribution, QR bottom.
  return (
    <svg viewBox="0 0 84 118" className="w-full h-auto border border-neutral-200" aria-hidden>
      <rect x="0" y="0" width="84" height="118" fill="#F9F8F6" />
      <rect x="0" y="0" width="84" height="2" fill="#DA6060" />
      <rect x="6" y="6" width="26" height="2" fill="#DA6060" />
      <text x="6" y="24" fontSize="18" fill="#DA6060" fontFamily="Times, serif" fontWeight="bold">“</text>
      <rect x="14" y="30" width="60" height="3" fill="#1A1A1A" opacity="0.8" />
      <rect x="14" y="36" width="55" height="3" fill="#1A1A1A" opacity="0.8" />
      <rect x="14" y="42" width="45" height="3" fill="#1A1A1A" opacity="0.8" />
      <rect x="14" y="52" width="34" height="2" fill="#5A554E" />
      <rect x="6" y="60" width="72" height="0.5" fill="#00000018" />
      <rect x="18" y="68" width="48" height="3" fill="#1A1A1A" />
      <rect x="24" y="74" width="36" height="1.8" fill="#5A554E" opacity="0.7" />
      <rect x="28" y="82" width="28" height="28" fill="#1A1A1A" />
      <rect x="31" y="85" width="4" height="4" fill="#F9F8F6" />
      <rect x="45" y="85" width="8" height="4" fill="#F9F8F6" />
      <rect x="31" y="99" width="8" height="4" fill="#F9F8F6" />
      <rect x="49" y="103" width="4" height="4" fill="#F9F8F6" />
    </svg>
  );
}

const PREVIEW = { classic: ClassicPreview, banner: BannerPreview, quote: QuotePreview };
const ICON = { classic: Printer, banner: Printer, quote: Quote };

export default function PosterTemplatesSection({ refCode }) {
  const { t } = useT();
  const [posterLang, setPosterLang] = useState("nl");
  const [customQuote, setCustomQuote] = useState("");
  const [customAttrib, setCustomAttrib] = useState("");
  const [busyVariant, setBusyVariant] = useState(null);

  const download = async (variant) => {
    setBusyVariant(variant);
    try {
      const params = { lang: posterLang, variant };
      if (variant === "quote") {
        if (customQuote.trim()) params.quote_text = customQuote.trim();
        if (customAttrib.trim()) params.quote_attribution = customAttrib.trim();
      }
      const r = await api.get("/affiliate/poster.pdf", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jobgrid-${variant}-${refCode || "affiliate"}-${posterLang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("affPosterDownloaded"));
    } catch {
      toast.error(t("affPosterDownloadFailed"));
    } finally {
      setBusyVariant(null);
    }
  };

  const variants = [
    { key: "classic", labelKey: "affPosterClassic", descKey: "affPosterClassicDesc" },
    { key: "banner", labelKey: "affPosterBanner", descKey: "affPosterBannerDesc" },
    { key: "quote", labelKey: "affPosterQuote", descKey: "affPosterQuoteDesc" },
  ];

  return (
    <div className="mt-6 border-t border-neutral-200 pt-5" data-testid="poster-templates-section">
      <p className="ed-eyebrow flex items-center gap-2">
        <Printer size={12} strokeWidth={1.75} /> {t("affPosterEyebrow")}
      </p>
      <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed max-w-2xl">
        {t("affPosterHelp")}
      </p>

      {/* Language chooser — the poster language is decoupled from the UI
          language so a Dutch affiliate can still print an English poster
          for an international placement. */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="poster-lang-picker">
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mr-1">
          {t("affPosterLangLabel")}
        </span>
        {LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setPosterLang(l.code)}
            data-testid={`poster-lang-${l.code}`}
            className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[0.65rem] transition-colors ${
              posterLang === l.code
                ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                : "border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500"
            }`}
          >
            <span>{l.flag}</span>
            <span className="uppercase tracking-wider">{l.code}</span>
            {posterLang === l.code && <Check size={10} strokeWidth={2.5} />}
          </button>
        ))}
      </div>

      {/* Variant cards */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        {variants.map(({ key, labelKey, descKey }) => {
          const Preview = PREVIEW[key];
          const Icon = ICON[key];
          return (
            <div
              key={key}
              data-testid={`poster-variant-${key}`}
              className="border border-neutral-200 bg-white p-4 flex flex-col"
            >
              <div className="w-full max-w-[160px] mx-auto">
                <Preview />
              </div>
              <p className="mt-3 font-serif text-base leading-tight text-neutral-900">
                {t(labelKey)}
              </p>
              <p className="mt-1 text-xs text-neutral-600 leading-relaxed flex-1">
                {t(descKey)}
              </p>
              <button
                type="button"
                onClick={() => download(key)}
                disabled={busyVariant === key}
                data-testid={`poster-download-${key}`}
                className="ed-btn ed-btn-outline mt-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Icon size={12} strokeWidth={1.75} />
                {busyVariant === key ? t("affPosterDownloading") : t("affPosterDownloadPdf")}
              </button>
            </div>
          );
        })}
      </div>

      {/* Custom quote form — visible always but only used by the quote
          variant download. Empty fields fall back to the curated default. */}
      <div className="mt-5 border border-dashed border-neutral-300 bg-neutral-50 p-4" data-testid="poster-custom-quote-form">
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 flex items-center gap-1.5">
          <Quote size={11} strokeWidth={2} /> {t("affPosterCustomQuoteLabel")}
        </p>
        <p className="text-xs text-neutral-600 mt-1.5">{t("affPosterCustomQuoteHelp")}</p>
        <textarea
          data-testid="poster-custom-quote-text"
          value={customQuote}
          onChange={(e) => setCustomQuote(e.target.value)}
          rows={3}
          maxLength={220}
          placeholder={t("affPosterCustomQuotePlaceholder")}
          className="mt-2 w-full border border-neutral-300 bg-white p-2.5 font-serif italic text-sm text-neutral-800 focus:outline-none focus:border-neutral-900"
        />
        <input
          data-testid="poster-custom-quote-attribution"
          value={customAttrib}
          onChange={(e) => setCustomAttrib(e.target.value)}
          maxLength={80}
          placeholder={t("affPosterCustomAttribPlaceholder")}
          className="mt-2 w-full border border-neutral-300 bg-white p-2.5 font-mono text-xs text-neutral-800 focus:outline-none focus:border-neutral-900"
        />
        <p className="mt-2 font-mono text-[0.6rem] text-neutral-500">
          {customQuote.length}/220 · {customAttrib.length}/80
        </p>
      </div>
    </div>
  );
}
